/* eslint-disable no-empty */
import React from 'react'
import { MkGenericValue } from '@typescript'
import { EMkStorageKeys, DataStore } from '@utils/DataStore'
import { useDevUi } from '@utils/DevUIPanel/DevUiProvider'
import { ProtectedDataStore } from '@utils/ProtectedDataStore'
import { StepName } from '@account/components/Register/types'
import { validateStepByName, getStepIndexByName } from '@account/components/Register/stepRegistry'
import { useAuthLabelsContext } from '@account/contexts/AuthLabelsContext'
import { ERegistrationAction, RegistrationAction, RegistrationState, registrationReducer, RegistrationContext, createInitialState } from './_reducer'
import { validateField } from './fieldValidators'

/**
 * This hook is a wrapper around the pure `registrationReducer`.
 * It initialises state, exposes memo-friendly dispatch helpers and
 * provides a few derived selectors for convenience.
 */

// Re-export for external consumers (tests, other modules)
export { createInitialState }

/* ---------- Hook ---------- */
export const useRegistration = () => {
  const [state, dispatch] = React.useReducer<React.Reducer<RegistrationState, RegistrationAction>>(registrationReducer, createInitialState())
  const dev = useDevUi()
  const devBypass = dev.enabled && dev.bypassValidation
  const { getValidationMessage } = useAuthLabelsContext()
  const validateOptions = React.useMemo(() => ({ getValidationMessage }), [getValidationMessage])

  // Use ref to store current form data to avoid dependency issues
  const formDataRef = React.useRef(state.formData)
  formDataRef.current = state.formData

  // When CMS auth labels load, recompute verification code message (avoids stale fallback text in form state).
  // Do not run this for a pristine empty field: validateField would mark it invalid and show errors on initial load.
  React.useEffect(() => {
    if (devBypass) return
    const currentFormData = formDataRef.current
    const vc = currentFormData.verificationCode
    if (vc == null) return
    const res = validateField('verificationCode', vc.value, currentFormData, validateOptions)
    const valueStr = String(vc.value ?? '').trim()
    if (valueStr === '' && vc.isValid && !res.isValid) {
      return
    }
    if (res.isValid === vc.isValid && (res.message || '') === (vc.validationMessage || '')) return
    dispatch({
      type: ERegistrationAction.SET_FIELD_VALUE,
      payload: {
        fieldName: 'verificationCode',
        value: vc.value,
        isValid: res.isValid,
        validationMessage: res.message,
      },
    })
  }, [getValidationMessage, devBypass, validateOptions])

  // Use ref to store current step to avoid dependency issues
  const currentStepRef = React.useRef(state.currentStepName)
  currentStepRef.current = state.currentStepName

  /* Dispatch helpers */
  const updateField = React.useCallback(
    (fieldName: string, value: MkGenericValue) => {
      const currentFormData = formDataRef.current

      const primary = devBypass ? { isValid: true, message: '' } : validateField(fieldName, value, currentFormData, validateOptions)

      dispatch({
        type: ERegistrationAction.SET_FIELD_VALUE,
        payload: {
          fieldName,
          value,
          isValid: primary.isValid,
          validationMessage: primary.message,
        },
      })

      // Save username to localStorage when userName field is updated
      if (fieldName === 'userName' && value && typeof value === 'string' && value.trim()) {
        DataStore.setItem('savedUsername', value.trim(), { store: localStorage })
      }

      // Validate dependent fields if their requirements may have changed
      const getDependentFields = (name: string): string[] => {
        switch (name) {
          case 'verificationMethod':
            return ['phone', 'contractNumber']
          case 'registerAsCompany':
            return ['firstName', 'lastName', 'companyName']
          case 'phoneCountry':
            return ['phone']
          default:
            return []
        }
      }

      const dependents = getDependentFields(fieldName)

      // Update dependent fields one by one to avoid complex state updates
      dependents.forEach((dep) => {
        const depValue = currentFormData[dep]?.value
        const res = validateField(dep, depValue, currentFormData, validateOptions)

        if (res.isValid !== currentFormData[dep]?.isValid || res.message !== currentFormData[dep]?.validationMessage) {
          dispatch({
            type: ERegistrationAction.SET_FIELD_VALUE,
            payload: {
              fieldName: dep,
              value: depValue,
              isValid: res.isValid,
              validationMessage: res.message,
            },
          })
        }
      })

      // Clear step-level errors on any user edit
      dispatch({ type: ERegistrationAction.SET_VALIDATION_ERRORS, payload: [] })

      // Mark current step as touched
      dispatch({ type: ERegistrationAction.SET_TOUCHED, payload: { step: currentStepRef.current, touched: true } })
    },
    [devBypass, validateOptions],
  )

  const setCurrentStep = React.useCallback(
    (stepName: StepName) => {
      dispatch({ type: ERegistrationAction.SET_CURRENT_STEP, payload: stepName })
    },
    [], // Remove all dependencies to prevent infinite loops
  )

  const gotoStep = React.useCallback((stepName: StepName) => dispatch({ type: ERegistrationAction.SET_CURRENT_STEP, payload: stepName }), [])

  const gotoStepByName = React.useCallback((stepName: StepName, params?: Record<string, any>) => {
    dispatch({ type: ERegistrationAction.SET_CURRENT_STEP, payload: stepName })

    // Mark the step as touched when navigating to it
    dispatch({ type: ERegistrationAction.SET_TOUCHED, payload: { step: stepName, touched: true } })

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        updateField(key, value)
      })
    }
  }, [])

  // Helper function to navigate to activation error step
  const gotoActivationError = React.useCallback(
    (errorMessage: string, isActivation: boolean = true) => {
      updateField('activationErrorMessage', errorMessage)
      updateField('isActivationFlow', isActivation)
      gotoStepByName(StepName.ACTIVATION_ERROR)
    },
    [updateField, gotoStepByName],
  )
  const setLoading = React.useCallback((loading: boolean) => dispatch({ type: ERegistrationAction.SET_LOADING, payload: loading }), [])
  const setError = React.useCallback((message: string, raw?: Error, voyager?: unknown) => dispatch({ type: ERegistrationAction.SET_ERROR, payload: { message, raw, voyager } }), [])
  const clearError = React.useCallback(() => dispatch({ type: ERegistrationAction.CLEAR_ERROR }), [])
  const setRegistrationToken = React.useCallback((token: string | null) => dispatch({ type: ERegistrationAction.SET_TOKEN, payload: token }), [])
  const setSubmitted = React.useCallback((submitted: boolean) => dispatch({ type: ERegistrationAction.SET_SUBMITTED, payload: submitted }), [])
  const setValidationErrors = React.useCallback((errors: string[]) => dispatch({ type: ERegistrationAction.SET_VALIDATION_ERRORS, payload: errors }), [])

  const setFieldError = React.useCallback((fieldName: string, message: string) => dispatch({ type: ERegistrationAction.SET_FIELD_ERROR, payload: { fieldName, message } }), [])

  // Registration context persistence functions using ProtectedDataStore
  const REGISTRATION_CONTEXT_EXPIRY_HOURS = 24

  const saveRegistrationContext = React.useCallback((context: Omit<RegistrationContext, 'createdAt' | 'expiresAt'>) => {
    try {
      const now = Date.now()
      const expiresAt = now + REGISTRATION_CONTEXT_EXPIRY_HOURS * 60 * 60 * 1000

      const fullContext: RegistrationContext = {
        ...context,
        createdAt: now,
        expiresAt,
      }

      // Use ProtectedDataStore with localStorage and expiration time
      ProtectedDataStore.setItem(EMkStorageKeys.MAKO_REGISTRATION_CONTEXT, fullContext, {
        store: localStorage,
        expirationTime: expiresAt,
      })
    } catch (error) {}
  }, [])

  const loadRegistrationContext = React.useCallback((): RegistrationContext | null => {
    try {
      // Use ProtectedDataStore to load encrypted data with automatic expiration handling
      const context = ProtectedDataStore.getItem<RegistrationContext>(EMkStorageKeys.MAKO_REGISTRATION_CONTEXT, {
        store: localStorage,
      })

      if (!context) {
        return null
      }

      return context
    } catch (error) {
      return null
    }
  }, [])

  const clearRegistrationContext = React.useCallback(() => {
    try {
      ProtectedDataStore.removeItem(EMkStorageKeys.MAKO_REGISTRATION_CONTEXT, {
        store: localStorage,
      })
    } catch (error) {}
  }, [])

  const hasValidRegistrationContext = React.useCallback((): boolean => {
    const context = loadRegistrationContext()
    return context !== null
  }, [loadRegistrationContext])

  const extractActivationToken = React.useCallback(() => {
    if (typeof window === 'undefined') return null
    const urlParams = new URLSearchParams(window.location.search)
    const token = urlParams.get('token') || urlParams.get('at')
    if (token && !state.formData.activationToken?.value) {
      updateField('activationToken', token)
      updateField('isActivationFlow', true)
      return token
    }
    return null
  }, [updateField, state.formData.activationToken?.value, state.formData.isActivationFlow?.value])

  React.useEffect(() => {
    if (!state.formData.isActivationFlow?.value) {
      extractActivationToken()
    }
  }, [extractActivationToken, state.formData.isActivationFlow?.value])

  const isStepTouched = Boolean(state.touchedByStep?.[state.currentStepName])
  const resetState = React.useCallback(() => dispatch({ type: ERegistrationAction.RESET_STATE }), [])
  const setSelectedFactor = React.useCallback((factor: any) => dispatch({ type: ERegistrationAction.SET_SELECTED_FACTOR, payload: factor }), [])
  const setMfaFactors = React.useCallback((factors: any[]) => dispatch({ type: ERegistrationAction.SET_MFA_FACTORS, payload: factors }), [])
  const setUser = React.useCallback((user: any) => dispatch({ type: ERegistrationAction.SET_USER, payload: user }), [])
  const setMfaEnabled = React.useCallback((enabled: boolean) => dispatch({ type: ERegistrationAction.SET_MFA_ENABLED, payload: enabled }), [])
  const registrationMfaEnabled = React.useMemo(() => state.registrationMfaEnabled, [state.registrationMfaEnabled])
  const canSubmit = React.useMemo(() => {
    // Always validate the step - form should be submittable if valid,
    // even if not touched (e.g., from autofill or pre-populated values)
    const isValid = validateStepByName(state.currentStepName, state.formData as any)
    return isValid
  }, [state.currentStepName, state.formData, isStepTouched])

  return {
    state,
    dispatch,
    // helpers
    updateField,
    setFieldTouched: (touched: boolean) => dispatch({ type: ERegistrationAction.SET_TOUCHED, payload: { step: state.currentStepName, touched } }),
    setCurrentStep,
    gotoStep,
    getStepIndexByName,
    gotoStepByName,
    setLoading,
    setError,
    clearError,
    setRegistrationToken,
    setSubmitted,
    setValidationErrors,
    setFieldError,
    gotoActivationError,
    canSubmit,
    setSelectedFactor,
    setMfaFactors,
    setUser,
    setMfaEnabled,
    registrationMfaEnabled,
    saveRegistrationContext,
    loadRegistrationContext,
    clearRegistrationContext,
    hasValidRegistrationContext,
    resetState,
  }
}
